import { useCallback, useEffect } from 'react';
import { toString } from 'lodash';

import { FileImage } from '@/common/constants';
import prepareFile from '@/components/controls/DropScan/prepareFile';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { IDocumentFilesUrlDto } from '@/dtos';
import { getBlob } from '@/modules/transaction/hooks/useFileUploadQuery';
import useQRCodeStore from '@/modules/UseMyMoney/widgets/QrCode/stores/useQRCode.store';
import { getFileNameFromPath, safeDecodeURIComponent } from '@/utils/modifiers';

interface Props {
  document?: IDocumentFilesUrlDto;
  storeKey: string;
  onFileReceivedByQrCode?: () => void;
}

const useSetFileFromQrCode = ({ document, storeKey, onFileReceivedByQrCode }: Props) => {
  const {
    state: { qrCodeId },
    setState: setQRCodeState,
    handleReset: resetQRCodeState,
  } = useQRCodeStore();

  const {
    values: { files: documentFiles, documentId },
    handleSetValues: setFilesInDocumentState,
  } = useFilesInDocumentStore(storeKey);

  const handleSetFileFromQrCodeStream = useCallback((filesFromStream: FileImage) => {
    if (!documentId) {
      setFilesInDocumentState({ documentId: toString(document?.document_id) });
    }
    setQRCodeState({ documentId: toString(document?.document_id), qrCodeId });
    setFilesInDocumentState({ files: [...documentFiles, filesFromStream] });
    onFileReceivedByQrCode?.();
  }, [
    documentId,
    setQRCodeState,
    setFilesInDocumentState,
    documentFiles,
    document,
    onFileReceivedByQrCode,
    qrCodeId,
  ]);

  const handleGetFileFromQrCodeStream = useCallback(async (value: IDocumentFilesUrlDto) => {
    const fileUrl = value?.files?.[0]?.s3_url;
    const fileId = toString(value?.files?.[0]?.id);
    const currentFileName = getFileNameFromPath(fileUrl);
    const decodedFileName = safeDecodeURIComponent(currentFileName);

    if (!fileUrl || !fileId) return;

    const isFileAlreadyExist = documentFiles.find((file) => file.id === fileId);

    if (!isFileAlreadyExist) {
      const fileBlob = await getBlob(fileUrl);

      const fileImage = prepareFile({
        id: fileId,
        name: decodedFileName || '',
        temporalId: fileId,
        url: fileUrl,
        type: fileBlob.type,
        blob: fileBlob,
      });

      handleSetFileFromQrCodeStream(fileImage);
    }
  }, [handleSetFileFromQrCodeStream, documentFiles]);

  useEffect(() => {
    if (document) {
      handleGetFileFromQrCodeStream(document);
    }
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [document]);
  
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  useEffect(() => () => resetQRCodeState(), []);
};

export default useSetFileFromQrCode;
